// AssetModelPeek — the product's ONE home (peek-first, architect-ruled: no
// full page; fine-tuned progressively, especially once artifact files land).
// Rides the shared PeekHost (`?peek=asset-models:<id>`) on the ContextPanel
// grammar. Sections: identity · datasheet numbers · connectivity · documents
// (the slot exists from day one — evidence/files arrive with the catalog
// content store + the documents packs) · usage. The acts are the product's ONE
// registry (`AssetModelActions.tsx`) — a peek-only entity (`ContextPanel
// pageless`): a LOCAL product's edits sit on their cards (Identity · Connectivity ·
// Documents), its lifecycle in the door; a CANONICAL one says it is the catalog's
// at the body's top.

import { useTranslation } from 'react-i18next';

import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import type { AssetModelProjectItem } from '@ems-app/schemas/catalog/responses';
import { CATEGORY_ICON } from '../../components/patterns/assetsDisplay.js';
import { ContextPanel } from '../../components/patterns/ContextPanel.js';
import { DescriptionList, Field } from '../../components/patterns/DescriptionList.js';
import { DetailSection } from '../../components/patterns/DetailSection.js';
import { EntityRef } from '../../components/patterns/EntityRef.js';
import { SectionAct } from '../../components/patterns/SectionAct.js';
import type { EntityAct } from '../../components/patterns/entityRegistry.js';
import { Badge } from '../../components/ui/Badge.js';
import { icons } from '../../components/ui/icons.js';
import { Tag } from '../../components/ui/Tag.js';
import { useEntityPeek } from '../../hooks/useEntityPeek.js';
import { useAssetModel } from '../../hooks/useAssetModels.js';
import { useAssetModelActions } from './AssetModelActions.js';
import { TierBadge, showsDeviceHalf } from '../../components/patterns/catalogDisplay.js';
import { ModelDocumentsSection } from './ModelDocumentsSection.js';

export function AssetModelPeek({ projectId, modelId, onClose }: { projectId: string; modelId: string; onClose: () => void }) {
  const { t } = useTranslation('catalog');
  const { t: ta } = useTranslation('assets');
  const q = useAssetModel(projectId, modelId);
  const m = q.data;
  const { acts, overlays } = useAssetModelActions(projectId, m);
  const Glyph = m ? CATEGORY_ICON[m.category as AssetCategory] : null;

  return (
    <>
      <ContextPanel
        host="peek"
        onClose={onClose}
        leading={Glyph ? <Glyph size={22} aria-hidden className="text-[var(--color-text-secondary)]" /> : undefined}
        kind={m ? `${ta(`category.${m.category}`)}${m.manufacturer ? ` · ${m.manufacturer}` : ''}` : undefined}
        title={m?.name ?? '…'}
        status={
          m ? (
            <>
              <TierBadge m={m} />
              {m.is_disabled && (
                <Badge variant="neutral" appearance="subtle">
                  {t('store.notOffered')}
                </Badge>
              )}
            </>
          ) : undefined
        }
        acts={acts}
        pageless
      >
        {q.isError ? (
          <p role="alert" className="text-sm text-[var(--color-danger)]">{t('peek.error')}</p>
        ) : !m ? (
          <p className="text-sm text-[var(--color-text-secondary)]">{t('common:statuses.loading')}</p>
        ) : (
          <PeekBody m={m} projectId={projectId} acts={acts} />
        )}
      </ContextPanel>
      {overlays}
    </>
  );
}

// Exported for the Workbench judging story (`Workbench/Product peek`) — the
// story renders THIS body (never a re-roll) inside a replicated shell (its
// defaults keep the story query-free: no project, no acts). `acts` are the
// product's registry — each card carries its own (`SectionAct`).
export function PeekBody({ m, projectId = '', acts = [] }: { m: AssetModelProjectItem; projectId?: string; acts?: readonly EntityAct[] }) {
  const { t } = useTranslation('catalog');
  const { t: ta } = useTranslation('assets');
  // The successor is FOLLOWED inside the sheet — this product joins the way back.
  const { followPeek } = useEntityPeek();
  const dash = <span className="text-[var(--color-text-secondary)]">—</span>;
  // Lifecycle = the vendor's market state (record content): a FACT among the
  // identity facts, rendered ONLY off `current` (exception marking); tokens
  // are content-tolerant — an unknown status renders raw rather than hiding.
  const lifecycle = m.lifecycle && m.lifecycle.status !== 'current' ? m.lifecycle : null;
  const iface = m.interfaces;
  const specRows: Array<[string, string]> = [];
  if (m.power_kw != null) specRows.push([ta('form.model.spec.power_kw'), String(m.power_kw)]);
  if (m.capacity_kva != null) specRows.push([ta('form.model.spec.capacity_kva'), String(m.capacity_kva)]);
  if (m.capacity_ah != null) specRows.push([ta('form.model.spec.capacity_ah'), String(m.capacity_ah)]);
  if (m.voltage_v != null) specRows.push([ta('form.model.spec.voltage_v'), String(m.voltage_v)]);
  if (m.watt_peak_w != null) specRows.push([ta('form.model.spec.watt_peak_w'), String(m.watt_peak_w)]);
  if (m.cooling_kw != null) specRows.push([ta('form.model.spec.cooling_kw'), String(m.cooling_kw)]);
  if (m.tank_l != null) specRows.push([ta('form.model.spec.tank_l'), String(m.tank_l)]);
  if (m.battery_chemistry) specRows.push([ta('form.model.chemistry'), ta(`form.model.batteryChem.${m.battery_chemistry}`)]);
  if (m.battery_technology) specRows.push([ta('form.model.technology'), ta(`form.model.batteryTechnology.${m.battery_technology}`)]);
  if (m.warranty_months != null) specRows.push([ta('form.model.warrantyMonths'), String(m.warranty_months)]);
  return (
    <div className="flex flex-col gap-3">
      {/* A catalog record is the catalog's — said first, for every viewer (object
          truth, not permission words); a withdrawn one adds that only a release
          brings it back. */}
      {m.is_canonical && (
        <div className="flex flex-col gap-1">
          <p className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)]">
            <icons.locked size={14} aria-hidden className="shrink-0" />
            {t('peek.readOnly')}
          </p>
          {m.is_disabled && <p className="text-sm text-[var(--color-text-secondary)]">{t('peek.withdrawnNote')}</p>}
        </div>
      )}
      <DetailSection dense title={t('peek.sections.identity')} action={<SectionAct acts={acts} section="identity" />}>
        <DescriptionList>
          <Field label={t('peek.series')}>{m.series ? <span dir="ltr">{m.series}</span> : dash}</Field>
          <Field label={t('peek.partNumber')}>
            {m.part_number ? <span dir="ltr" className="font-mono">{m.part_number}</span> : dash}
          </Field>
          {lifecycle && (
            <Field label={t('peek.lifecycle')}>
              <span className="inline-flex flex-wrap items-center gap-1">
                {t(`peek.lifecycleStatus.${lifecycle.status}`, { defaultValue: lifecycle.status })}
                {m.lifecycle_successor && (
                  <>
                    <span className="text-[var(--color-text-secondary)]">
                      {' · '}
                      {t('peek.succeededBy')}
                    </span>
                    <EntityRef
                      name={m.lifecycle_successor.name}
                      onOpen={() => followPeek(`asset-models:${m.lifecycle_successor!.id}`, m.name)}
                    />
                  </>
                )}
              </span>
            </Field>
          )}
        </DescriptionList>
      </DetailSection>
      {specRows.length > 0 && (
        <DetailSection dense title={t('peek.sections.specs')}>
          <DescriptionList>
            {specRows.map(([label, value]) => (
              <Field key={label} label={label}>{value}</Field>
            ))}
          </DescriptionList>
        </DetailSection>
      )}
      {/* Structurally absent on non-networked products (eligibility, not
          emptiness — the capability-scoped-tabs law); a networked product
          with nothing declared keeps the nudge below. */}
      {showsDeviceHalf(m) && (
      <DetailSection dense title={t('peek.sections.connectivity')} action={<SectionAct acts={acts} section="connectivity" />}>
        {m.capabilities.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-1">
              {m.capabilities.map((c) => (
                <Tag key={c}>{ta(`capability.${c}`)}</Tag>
              ))}
            </div>
            <DescriptionList>
              {iface.sims != null && <Field label={t('peek.sims')}>{iface.sims}</Field>}
              {iface.esim && <Field label={t('peek.esim')}>{t('value.yes', { ns: 'common' })}</Field>}
              {iface.ethernet && (
                <Field label={t('peek.ethernet')}>
                  {iface.ethernet.ports}
                  {iface.ethernet.wan ? ` · ${t('peek.wan')} ${iface.ethernet.wan}` : ''}
                </Field>
              )}
              {iface.serial && (
                <Field label={t('peek.serial')}>
                  {[iface.serial.rs232 ? `RS-232 × ${iface.serial.rs232}` : null, iface.serial.rs485 ? `RS-485 × ${iface.serial.rs485}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </Field>
              )}
              {iface.fronts && <Field label={t('peek.fronts')}>{t('value.yes', { ns: 'common' })}</Field>}
            </DescriptionList>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-text-secondary)]">{t('peek.capabilitiesNone')}</p>
        )}
      </DetailSection>
      )}
      {/* Documents — the record's sha-addressed artifacts (openable when the
          pack landed) + the evidence citations; a LOCAL product's own paper
          through the files module, with its Manage face (ModelDocumentsSection). */}
      <ModelDocumentsSection projectId={projectId} m={m} acts={acts} />
      <DetailSection dense title={t('peek.sections.usage')}>
        <p className="text-sm text-[var(--color-text-primary)]">
          {m.in_use_count > 0 ? t('peek.usedHere', { count: m.in_use_count }) : t('peek.usedNone')}
        </p>
      </DetailSection>
      {/* Free prose reads last — and only when it exists. Both tiers speak
          here: a local's description is user-written; a canonical's notes are
          the catalog record's curator knowledge (efficiency grades, derate
          onsets — real datasheet context). */}
      {m.description && (
        <DetailSection dense title={t('peek.sections.description')}>
          <p className="whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">{m.description}</p>
        </DetailSection>
      )}
      {m.notes && (
        <DetailSection dense title={t('peek.sections.catalogNotes')}>
          <p dir="ltr" className="whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">{m.notes}</p>
        </DetailSection>
      )}
      {/* Locals keep their model-request note; the canonical read-only line leads the body. */}
      {!m.is_canonical && <p className="text-xs text-[var(--color-text-secondary)]">{t('peek.localNote')}</p>}
    </div>
  );
}
